import React from "react";
import LegalShell, { LegalSection, LegalList } from "@/components/LegalShell";

// ============================================================================
// /terms-of-service — public legal page (no login required).
// Static content. Update CONTACT_EMAIL / COMPANY_NAME / JURISDICTION below
// to match your registered legal entity before launch.
// ============================================================================
const CONTACT_EMAIL = "rigsentinel@gmail.com";
const COMPANY_NAME = "RigSentinel";
const JURISDICTION = "the operator's country of registration";

export default function TermsOfService() {
  return (
    <LegalShell
      title="Terms of Service"
      subtitle={`These Terms of Service ("Terms") govern your access to and use of ${COMPANY_NAME} ("RigSentinel", "we", "us", "our"), a professional oilfield calculation Progressive Web Application. By creating an account, subscribing, or using the application in any way, you agree to be bound by these Terms.`}
      lastUpdated="February 2026"
      testId="page-terms-of-service"
    >
      <LegalSection id="acceptance" title="1. Acceptance of Terms">
        <p>
          By accessing or using RigSentinel, you confirm that you have read,
          understood, and agree to be bound by these Terms, our Privacy Policy,
          our Refund Policy, and our Disclaimer. If you do not agree, do not
          use the application.
        </p>
        <p>
          You confirm that you are at least 18 years of age and have full legal
          authority to enter into these Terms on your own behalf or on behalf
          of the organization you represent.
        </p>
      </LegalSection>

      <LegalSection id="service" title="2. Description of Service">
        <p>
          RigSentinel is a Progressive Web Application providing deterministic,
          formula-based calculation support and report templates for oilfield
          drilling, well control, mud, cementing, casing, and related
          operations. RigSentinel also offers AI-assisted document polishing
          (text rewriting and translation of user-supplied notes) and PDF /
          CSV export utilities.
        </p>
        <p>
          RigSentinel is a calculation-support and reference tool only. It is
          not a real-time operational control system, not a well-control
          decision system, and not a substitute for qualified engineering
          judgment, approved company procedures, regulatory requirements, or
          OEM data.
        </p>
      </LegalSection>

      <LegalSection id="license" title="3. License">
        <p>
          Subject to your compliance with these Terms and timely payment of
          applicable subscription fees, we grant you a limited, personal,
          non-exclusive, non-transferable, non-sublicensable, revocable license
          to access and use RigSentinel for your internal professional or
          personal field use.
        </p>
        <p>
          You may not (a) reverse engineer, decompile, or disassemble the
          application; (b) resell, rent, lease, or sublicense access; (c) use
          the application to build a competing product; or (d) remove or alter
          any proprietary notices.
        </p>
      </LegalSection>

      <LegalSection id="accounts" title="4. Accounts and Security">
        <p>
          Account access uses passwordless email magic-link authentication. You
          are responsible for maintaining the confidentiality of your email
          inbox and any active sessions. You must notify us immediately of any
          unauthorized account access.
        </p>
        <p>
          You are responsible for all activity that occurs under your account,
          including all calculations performed and documents generated.
        </p>
      </LegalSection>

      <LegalSection id="subscriptions" title="5. Subscriptions and Billing">
        <p>
          Paid plans (currently Pro and Enterprise) are billed in
          advance on a recurring monthly or annual basis through our payment
          provider, Paddle (paddle.com). Prices are displayed on the Pricing page and may
          change with reasonable advance notice for future billing cycles.
        </p>
        <LegalList
          items={[
            "Subscriptions renew automatically until cancelled by you.",
            "You may cancel a recurring subscription at any time; cancellation takes effect at the end of the current paid period.",
            "Failed or reversed payments may result in suspension or downgrade of access until payment is restored.",
            "Applicable taxes (VAT, GST, sales tax) may be added at checkout based on your billing location.",
          ]}
        />
        <p>
          Refund eligibility is governed by our{" "}
          <a
            href="/refund-policy"
            className="underline"
            style={{ color: "var(--rs-amber)" }}
            data-testid="terms-link-refund"
          >
            Refund Policy
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection id="acceptable-use" title="6. Acceptable Use">
        <p>You agree NOT to:</p>
        <LegalList
          items={[
            "Use the application for any unlawful purpose or in violation of any applicable regulation.",
            "Attempt to gain unauthorized access to any portion of the application, other users' accounts, or our infrastructure.",
            "Use the application to make real-time well-control, BOP, choke, pump, or safety-critical operational decisions without independent verification by qualified, certified personnel.",
            "Submit content that is unlawful, defamatory, infringing, or that contains personal data of third parties without lawful basis.",
            "Use automated scraping, crawling, or load-testing tools against the application without prior written permission.",
          ]}
        />
      </LegalSection>

      <LegalSection id="user-content" title="7. User Content">
        <p>
          You retain ownership of the data and notes you enter into RigSentinel
          ("User Content"). Calculator inputs, report drafts, well names, and
          field notes are stored locally in your browser using local storage
          and are not transmitted to our servers, except for content you
          explicitly send to the AI polishing or translation endpoints.
        </p>
        <p>
          You grant us a limited, non-exclusive license to process User Content
          that you submit to the AI polishing or translation endpoints solely
          to provide that service to you.
        </p>
      </LegalSection>

      <LegalSection id="disclaimer-summary" title="8. Disclaimer of Warranties">
        <p>
          RigSentinel is provided "as is" and "as available" without warranties
          of any kind, whether express, implied, or statutory, including but
          not limited to warranties of merchantability, fitness for a
          particular purpose, accuracy, non-infringement, or uninterrupted
          availability.
        </p>
        <p>
          Refer to our{" "}
          <a
            href="/disclaimer"
            className="underline"
            style={{ color: "var(--rs-amber)" }}
            data-testid="terms-link-disclaimer"
          >
            Disclaimer
          </a>{" "}
          and the in-app Safety Center for the full scope of operational and
          safety-critical disclaimers.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="9. Limitation of Liability">
        <p>
          To the maximum extent permitted by applicable law, in no event shall
          RigSentinel, its officers, directors, employees, affiliates, or
          licensors be liable for any indirect, incidental, special,
          consequential, punitive, or exemplary damages, including but not
          limited to damages for lost profits, lost revenue, lost data, well
          downtime, NPT, equipment damage, environmental incidents, personal
          injury, or business interruption, arising out of or in connection
          with your use of, or inability to use, the application — even if we
          have been advised of the possibility of such damages.
        </p>
        <p>
          To the maximum extent permitted by applicable law, our aggregate
          liability for any and all claims arising out of or relating to these
          Terms or the application shall not exceed the total amount you paid
          to us in the twelve (12) months immediately preceding the event
          giving rise to the claim.
        </p>
      </LegalSection>

      <LegalSection id="indemnity" title="10. Indemnification">
        <p>
          You agree to defend, indemnify, and hold harmless RigSentinel and its
          affiliates from and against any claims, liabilities, damages, losses,
          and expenses (including reasonable attorneys' fees) arising out of
          or in any way connected with (a) your access to or use of the
          application, (b) your violation of these Terms, (c) operational,
          regulatory, or safety consequences of any decision you made in
          reliance on the application's output, and (d) your User Content.
        </p>
      </LegalSection>

      <LegalSection id="termination" title="11. Termination">
        <p>
          We may suspend or terminate your access to the application
          immediately, without prior notice, for conduct that we believe
          violates these Terms or is harmful to other users, us, or third
          parties. Upon termination, your right to use the application ceases
          immediately. Sections that by their nature should survive
          termination (e.g., disclaimers, limitation of liability,
          indemnity, governing law) will survive.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="12. Changes to These Terms">
        <p>
          We may update these Terms from time to time. Material changes will
          be communicated through the application or by email to the address
          on your account. Your continued use of the application after the
          effective date of any change constitutes acceptance of the updated
          Terms.
        </p>
      </LegalSection>

      <LegalSection id="governing-law" title="13. Governing Law and Disputes">
        <p>
          These Terms are governed by the laws of {JURISDICTION}, without
          regard to its conflict-of-laws principles. Any dispute arising from
          or relating to these Terms or the application shall be resolved
          through good-faith negotiation; failing that, by the competent
          courts of {JURISDICTION}.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="14. Contact">
        <p>
          Questions about these Terms can be sent to{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline"
            style={{ color: "var(--rs-amber)" }}
            data-testid="terms-contact-email"
          >
            {CONTACT_EMAIL}
          </a>{" "}
          or via our{" "}
          <a
            href="/contact"
            className="underline"
            style={{ color: "var(--rs-amber)" }}
            data-testid="terms-link-contact"
          >
            Contact page
          </a>
          .
        </p>
      </LegalSection>
    </LegalShell>
  );
}
